import { Link } from "@tanstack/react-router";
import { ExternalLink, Eye } from "lucide-react";

import type { ManualStatus } from "@/api/db/connection";
import type { ManualSummary, ManualSummaryRow } from "@/api/db/manuals";
import { ManualLabels } from "@/lib/labels/manuals";
import { cn } from "@/lib/utils";
import { format } from "@/lib/format";

const STATUS_BADGE: Record<ManualStatus, string> = {
	draft: "border-border bg-background text-muted-foreground",
	delivered: "border-info/40 bg-info/10 text-info",
	revoked: "border-destructive/40 bg-destructive/10 text-destructive",
};

function ManualRowLine({ row }: { row: ManualSummaryRow }) {
	const firstAccess = format.dateTime(row.firstAccessAt);

	return (
		<li className="flex flex-col gap-1.5 border-b border-border py-2.5 last:border-b-0">
			<div className="flex flex-wrap items-center gap-2">
				<span
					className={cn(
						"rounded-md border px-1.5 py-px text-xs font-medium leading-none",
						STATUS_BADGE[row.status],
					)}
				>
					{ManualLabels.status[row.status]}
				</span>

				{row.deliveredAt !== null && (
					<span className="font-mono text-xs text-muted-foreground">
						entregue {format.dateTime(row.deliveredAt)}
					</span>
				)}

				{row.revokedAt !== null && (
					<span className="font-mono text-xs text-destructive">
						revogado {format.dateTime(row.revokedAt)}
					</span>
				)}

				<span className="ml-auto inline-flex items-start gap-1.5 text-xs text-muted-foreground">
					<Eye className="size-3" />
					<span className="flex flex-col gap-0.5">
						{firstAccess === null && <span>{ManualLabels.notOpened}</span>}
						{firstAccess !== null && <span>{ManualLabels.openedAt(firstAccess)}</span>}
						{firstAccess !== null && <span>{ManualLabels.accessCount(row.accessCount)}</span>}
					</span>
				</span>
			</div>

			<Link
				to="/clinica/manual/$id"
				params={{ id: row.id }}
				className="inline-flex w-fit items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
			>
				<ExternalLink className="size-3" />
				{ManualLabels.openEditor}
			</Link>
		</li>
	);
}

export function ManualsList({ summary }: { summary: ManualSummary }) {
	if (summary.manuals.length === 0) {
		return <p className="text-sm text-muted-foreground">{ManualLabels.listEmpty}</p>;
	}

	return (
		<ul className="flex flex-col">
			{summary.manuals.map((row) => (
				<ManualRowLine key={row.id} row={row} />
			))}
		</ul>
	);
}
